<template>
    <div class="container">
        <div v-if="!isFormVisible" class="card">
            <div class="card-header d-flex justify-content-between align-items-center ps-4 pt-3 pb-4">
                <h5><i class="fa-duotone fa-solid fa-list"></i> Lista de Módulos</h5>
                <argon-button size="sm" color="primary" variant="gradient" @click="openCreateModal">
                    <span class="fa-solid fa-plus" />
                    Crear
                </argon-button>
            </div>
            <div class="card-body pt-0">
                <CustomFilters
:order-options="orderOptions" :items-per-page="itemsPerPage" :view-as-card="viewAsCard"
                    :show-deleted="showDeleted" @apply-filter="applyFilter" @update-items-per-page="updateItemsPerPage"
                    @update-view-as-card="updateViewAsCard" @update-show-deleted="updateShowDeleted" />
                <div v-if="loading" class="loading-indicator">
                    <p>Cargando datos...</p>
                </div>
                <div v-else>
                    <div v-if="filteredItems.length === 0" class="no-records p-5 px-4">
                        <p>No se encontraron módulos. Intenta ajustar los filtros o añadir nuevos módulos.</p>
                    </div>
                    <div v-else>
                        <div v-if="!viewAsCard" class="table-responsive">
                            <GenericTable
:data="filteredItems" :headers="headers" @delete-record="deleteItem"
                                @edit-record="openEditModal" @restore-record="restoreItem" />
                        </div>
                        <transition-group ref="listContainer" name="fade-move" tag="div" class="row me-2 ms-2 pt-2">
                            <template v-if="viewAsCard">
                                <AppCard
v-for="(item) in filteredItems" :key="item.id" ref="listItems" :data="item"
                                    class="fade-card" @delete-record="deleteItem" @edit-record="openEditModal"
                                    @restore-record="restoreItem" />
                            </template>
                        </transition-group>
                        <Pagination
:total-items="totalFilteredItems" :current-page="currentPage" :last-page="lastPage"
                            :items-per-page="itemsPerPage" @page-change="changePage" />
                    </div>
                </div>
            </div>
        </div>
        <ModelModal
v-if="showModal && !displayAsForm" v-model="showModal" :is-edit-mode="isEditMode"
            :resource-api="resourceApi" :entity-data="entityData" :display-as-form="displayAsForm"
            :resource-name="resourceName" @entity-updated="handleEntityUpdated">
            <template #default="{ entity, errors }">
                <div class="mb-3">
                    <label for="name" class="form-label">Nombre</label>
                    <input
id="name" v-model="entity.name" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.name }">
                    <div v-if="errors.name" class="invalid-feedback">
                        {{ errors.name[0] }}
                    </div>
                </div>
                <div class="mb-3">
                    <label for="description" class="form-label">Descripción</label>
                    <textarea
id="description" v-model="entity.description" class="form-control" rows="3"
                        :class="{ 'is-invalid': errors.description }"></textarea>
                    <div v-if="errors.description" class="invalid-feedback">
                        {{ errors.description[0] }}
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3">
                        <div class="mb-3">
                            <label for="subscription" class="form-label">Suscripción</label>
                            <ToggleSwitch v-model="entity.subscription" :errors="errors" error-field-name="subscription" />
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="mb-3">
                            <label for="users" class="form-label">Usuarios</label>
                            <input
id="users" v-model.number="entity.users" type="number" class="form-control"
                                :class="{ 'is-invalid': errors.users }">
                            <div v-if="errors.users" class="invalid-feedback">
                                {{ errors.users[0] }}
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="mb-3">
                            <label for="space" class="form-label">Espacio (MB)</label>
                            <input
id="space" v-model.number="entity.space" type="number" class="form-control"
                                :class="{ 'is-invalid': errors.space }">
                            <div v-if="errors.space" class="invalid-feedback">
                                {{ errors.space[0] }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-3">
                            <label for="base_price" class="form-label">Precio Base</label>
                            <input
id="base_price" v-model.number="entity.pricing.base_price" type="number" step="0.01" 
                                class="form-control" :class="{ 'is-invalid': errors['pricing.base_price'] }">
                            <div v-if="errors['pricing.base_price']" class="invalid-feedback">
                                {{ errors['pricing.base_price'][0] }}
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="mb-3">
                            <label for="price_per_user" class="form-label">Precio por Usuario</label>
                            <input
id="price_per_user" v-model.number="entity.pricing.price_per_user" type="number" step="0.01" 
                                class="form-control" :class="{ 'is-invalid': errors['pricing.price_per_user'] }">
                            <div v-if="errors['pricing.price_per_user']" class="invalid-feedback">
                                {{ errors['pricing.price_per_user'][0] }}
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </ModelModal>
        <div v-if="isFormVisible && displayAsForm" class="card">
            <div class="card-header d-flex justify-content-between align-items-center ps-4 pt-3 pb-4">
                <h5><i class="fa-duotone fa-solid fa-list"></i> {{ isEditMode ? 'Editar' : 'Crear' }} Aplicación</h5>
                <argon-button size="sm" color="primary" variant="gradient" @click="closeForm">
                    <span class="fa-solid fa-arrow-left" />
                    Volver
                </argon-button>
            </div>
            <div class="card-body pt-0">
                <ModelModal
v-model="showModal" :is-edit-mode="isEditMode" :resource-api="resourceApi"
                    :entity-data="entityData" :display-as-form="displayAsForm" :keep-form-open="keepFormOpen"
                    :resource-name="resourceName" @entity-updated="handleEntityUpdated">
                    <template #default="{ entity, errors }">
                        <div class="mb-3">
                            <label for="name" class="form-label">Nombre</label>
                            <input
id="name" v-model="entity.name" type="text" class="form-control"
                                :class="{ 'is-invalid': errors.name }">
                            <div v-if="errors.name" class="invalid-feedback">
                                {{ errors.name[0] }}
                            </div>
                        </div>
                        <div class="mb-3">
                            <label for="description" class="form-label">Descripción</label>
                            <textarea
id="description" v-model="entity.description" class="form-control" rows="3"
                                :class="{ 'is-invalid': errors.description }"></textarea>
                            <div v-if="errors.description" class="invalid-feedback">
                                {{ errors.description[0] }}
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-3">
                                <div class="mb-3">
                                    <label for="subscription" class="form-label">Suscripción</label>
                                    <ToggleSwitch v-model="entity.subscription" :errors="errors" error-field-name="subscription" />
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="mb-3">
                                    <label for="users" class="form-label">Usuarios</label>
                                    <input
id="users" v-model.number="entity.users" type="number" class="form-control"
                                        :class="{ 'is-invalid': errors.users }">
                                    <div v-if="errors.users" class="invalid-feedback">
                                        {{ errors.users[0] }}
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="mb-3">
                                    <label for="space" class="form-label">Espacio (MB)</label>
                                    <input
id="space" v-model.number="entity.space" type="number" class="form-control"
                                        :class="{ 'is-invalid': errors.space }">
                                    <div v-if="errors.space" class="invalid-feedback">
                                        {{ errors.space[0] }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="base_price" class="form-label">Precio Base</label>
                                    <input
id="base_price" v-model.number="entity.pricing.base_price" type="number" step="0.01" 
                                        class="form-control" :class="{ 'is-invalid': errors['pricing.base_price'] }">
                                    <div v-if="errors['pricing.base_price']" class="invalid-feedback">
                                        {{ errors['pricing.base_price'][0] }}
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="price_per_user" class="form-label">Precio por Usuario</label>
                                    <input
id="price_per_user" v-model.number="entity.pricing.price_per_user" type="number" step="0.01" 
                                        class="form-control" :class="{ 'is-invalid': errors['pricing.price_per_user'] }">
                                    <div v-if="errors['pricing.price_per_user']" class="invalid-feedback">
                                        {{ errors['pricing.price_per_user'][0] }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </ModelModal>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useModelActions } from '@/composables/useModelActions';
import ModelModal from '@/components/ModelModal.vue';
import CustomFilters from '@/components/CustomFilters.vue';
import ArgonButton from '@/components/ArgonButton.vue';
import Pagination from '@/components/Pagination.vue';
import AppCard from './AppCard.vue';
import GenericTable from '@/components/GenericTable.vue';
import ToggleSwitch from '@/components/ToggleSwitch.vue';

const resourceApi = 'modules';
const resourceName = 'Módulo';

const orderOptions = [
    { text: 'Fecha de modificación', value: 'updated_at' },
    { text: 'Fecha de creación', value: 'created_at' },
    { text: 'Nombre', value: 'name' }
];

const headers = ref([
    { text: 'Nombre', value: 'name' },
    { text: 'Descripción', value: 'description' },
    { text: 'Suscripción', value: 'subscription' },
    { text: 'Usuarios', value: 'users' },
    { text: 'Espacio (MB)', value: 'space' },
    { text: 'Precio Base', value: 'pricing.base_price' },
    { text: 'Precio/Usuario', value: 'pricing.price_per_user' }
]);

const showModal = ref(false);
const isEditMode = ref(false);
const entityData = ref({});
const viewAsCard = ref(false);
const showDeleted = ref(false);
const loading = ref(true);
const displayAsForm = ref(false);
const keepFormOpen = ref(false);
const isFormVisible = ref(false);

const {
    openCreateModal: baseOpenCreateModal,
    openEditModal: baseOpenEditModal,
    handleEntityUpdated,
    applyFilter,
    updateItemsPerPage,
    updateViewAsCard,
    updateShowDeleted,
    restoreItem,
    deleteItem,
    fetchItems,
    changePage,
    filteredItems,
    totalFilteredItems,
    currentPage,
    itemsPerPage,
    lastPage
} = useModelActions(resourceApi, {
    showModal,
    isEditMode,
    entityData,
    viewAsCard,
    showDeleted,
    displayAsForm,
    loading,
    isFormVisible,
    keepFormOpen,
    resourceName
});

// Wrapper para openCreateModal con inicialización de pricing
const openCreateModal = (defaultValues = {}) => {
    const initialData = {
        ...defaultValues,
        pricing: {
            base_price: 0,
            price_per_user: 0
        },
        subscription: true,
        users: 5,
        space: 100
    };
    baseOpenCreateModal(initialData);
};

// Wrapper para openEditModal para asegurar que pricing existe
const openEditModal = (entity) => {
    const entityWithPricing = {
        ...entity,
        pricing: entity.pricing || {
            base_price: 0,
            price_per_user: 0
        }
    };
    baseOpenEditModal(entityWithPricing);
};

watch([itemsPerPage, currentPage], ([newItemsPerPage, newPage]) => {
    if (newItemsPerPage || newPage) {
        fetchItems(newPage);
    }
});

onMounted(async () => {
    await fetchItems();
});

const closeForm = () => {
    isFormVisible.value = false;
    fetchItems(currentPage.value);
};
</script>
